<!--
  ChequeFormModal: alta y edición de cheques de terceros cargados a mano.

  ENTRADA
    Props: no tiene.
    Métodos públicos (por ref):
      add()         Abre el alta vacía.
      edit(item)    Abre la edición. Si el cheque ingresó con un recibo, bloquea el importe.
      close()       Cierra el modal sin confirmar.

  SALIDA
    Eventos:
      @saved(response)   Se guardó. La vista recarga lo que corresponda.
    Efectos:
      Guarda con agregarCheque o editarCheque. No mueve caja: el cheque queda en cartera.

  USOS
    Vista: <ChequeFormModal ref="chequeForm" @saved="reloadAll()" />
    Alta:     this.$refs.chequeForm.add()
    Edición:  this.$refs.chequeForm.edit(item)
    Hoy se invoca desde finances/staff/cheques.vue: botón, listado y drawer.

  DEPENDENCIAS Y PERMISOS
    Componentes globales: AppIcon, app-text-input, app-number-input, app-textarea.
    No controla permisos: quien lo invoca decide si el usuario puede abrirlo.
-->
<template>
  <div>
    <!-- CRUD MAIN -->
    <b-modal v-model="modal.form.active"
            size="lg"
            :key="modal.form._id"
            :id="modal.form._id">                  
      <template #modal-header><div class="app-modal-header"><span class="app-modal-header__icon"><AppIcon :name="crud.form.id ? 'edit' : 'add'" /></span><h5 class="app-modal-header__title">{{ modal.form.title }}</h5></div></template>

      <div class="cheques-form__section"><AppIcon name="receipt_long" /> Datos del cheque</div>
      <b-row>
        <b-col md="5">
          <b-form-group label="Banco">
            <b-form-input type="text"
                          size="sm"
                          v-model="crud.form.bank"
                          placeholder="Ej.: Banco Macro" />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Número">
            <app-text-input size="sm"
                            v-model="crud.form.number"
                            :allowed-pattern="/[0-9]/g"
                            inputmode="numeric"
                            placeholder="N.º de cheque" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Importe" :description="crud.form.amount_locked ? 'Ingresó con un recibo: el importe se modifica desde el recibo.' : ''">
            <b-input-group prepend="$" size="sm" class="cheques-form__amount">
              <app-number-input v-model="crud.form.amount" :readonly="crud.form.amount_locked" />
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>

      <div class="cheques-form__section"><AppIcon name="event" /> Fechas</div>
      <b-row>
        <b-col md="4">
          <b-form-group label="Emisión">
            <b-form-input type="date" size="sm" v-model="crud.form.date" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Vencimiento">
            <b-form-input type="date" size="sm" v-model="crud.form.date_expiration" :min="crud.form.date" />
          </b-form-group>
        </b-col>
        <b-col md="4" class="d-flex align-items-center">
          <span v-if="formExpiration" class="cheques-form__expiration" :class="'cheques-form__expiration--' + formExpiration.tone">
            <AppIcon :name="formExpiration.icon" /> {{ formExpiration.text }}
          </span>
        </b-col>
      </b-row>

      <div class="cheques-form__section"><AppIcon name="person" /> Emisor</div>
      <b-row>
        <b-col md="8">
          <b-form-group label="Nombre / razón social">
            <b-form-input type="text"
                          size="sm"
                          v-model="crud.form.transmitter_name"
                          placeholder="Quién emitió el cheque" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="CUIT">
            <app-text-input size="sm"
                            v-model="crud.form.transmitter_documents_number"
                            :allowed-pattern="/[0-9-]/g"
                            inputmode="numeric"
                            placeholder="Ej.: 30-12345678-9" />
          </b-form-group>
        </b-col>
      </b-row>

      <div class="cheques-form__section"><AppIcon name="tune" /> Otros datos</div>
      <b-row>
        <b-col md="8">
          <b-form-group label="Observaciones">
            <app-textarea
              v-model="crud.form.observations"
              placeholder="Ingresar las observaciones..."
              rows="2"
              max-rows="4"></app-textarea>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <div class="cheques-form__switch">
            <b-form-checkbox v-model="crud.form.nd" switch size="sm">NO DECLARADO</b-form-checkbox>
            <small>Solo puede utilizarse desde un punto de venta no declarado y depositarse en una cuenta no declarada.</small>
          </div>
        </b-col>
      </b-row>

      <div slot="modal-footer">
        <b-button variant="outline-secondary" class="mr-1" @click="modal.form.active=false"><AppIcon name="x" class="mr-1" /> Cancelar</b-button>
        <b-button variant="dark" @click="save()"><AppIcon name="save" class="mr-1" /> Guardar</b-button>
      </div>
    </b-modal>
  </div>
</template>
<script>
  import serviceAPI from '../services'
  import Error from '@/handler/error'
  import moment from 'moment'

  export default {
    name: 'ChequeFormModal',
    data: () => {
      return {
        crud: {
          form: {
            id: 0,
            number: '',
            date: '',
            date_expiration: '',
            amount: 0,
            bank: '',
            transmitter_name: '',
            transmitter_documents_number: '',
            observations: '',
            nd: false,
            amount_locked: false,
          },                 
        },
        modal: {
          form: {
            active: false,
            title: ''
          },        
        },
      }
    },
    computed: {
      // estado del vencimiento en el formulario de alta/edición
      formExpiration() {
        if(!this.crud.form.date_expiration) return null
        const days = moment(this.crud.form.date_expiration).startOf('day').diff(moment().startOf('day'), 'days')
        if(days > 0) return { tone: days <= 15 ? 'warning' : 'success', icon: 'schedule', text: 'Vence en ' + days + (days == 1 ? ' día' : ' días') }
        if(days == 0) return { tone: 'warning', icon: 'today', text: 'Vence hoy' }
        return { tone: 'danger', icon: 'event_busy', text: 'Vencido hace ' + Math.abs(days) + (days == -1 ? ' día' : ' días') }
      },
    },
    methods: {
      close() {
        this.modal.form.active = false
      },
      add() {
        this.crud.form.id = 0
        this.crud.form.number = ''
        this.crud.form.date = ''
        this.crud.form.date_expiration = ''
        this.crud.form.amount = 0
        this.crud.form.bank = ''
        this.crud.form.transmitter_name = ''
        this.crud.form.transmitter_documents_number = ''
        this.crud.form.observations = ''
        this.crud.form.nd = false
        this.crud.form.amount_locked = false

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = "Nuevo Cheque"
        this.modal.form.active = true
      },
      edit(item) {
        this.crud.form.id = item.id
        this.crud.form.number = item.number
        this.crud.form.date = item.date
        this.crud.form.date_expiration = item.date_expiration
        this.crud.form.amount = item.amount
        this.crud.form.bank = item.bank
        this.crud.form.transmitter_name = item.transmitter_name
        this.crud.form.transmitter_documents_number = item.transmitter_documents_number
        this.crud.form.observations = item.observations
        this.crud.form.nd = item.nd
        // si ingresó con un recibo, el importe no se edita (lo valida también el backend)
        this.crud.form.amount_locked = this.hasOrigen(item)
        
        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = "Editar Cheque"
        this.modal.form.active = true
      },
      save() {
        let loader = this.$loading.show();
        if (this.crud.form.id) {
          var result = serviceAPI.editarCheque(this.crud.form);
        } else {
          var result = serviceAPI.agregarCheque(this.crud.form);
        }

        result.then((response) => {
          this.modal.form.active = false
          loader.hide()
          this.$emit('saved', response)

          this.$awn.success("Datos guardados con éxito");
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error));
        })
      },     
      hasOrigen(item) {
        return !!(item.origen && item.origen.customer)
      },
    },
  }
</script>
<style scoped>
  .cheques-form__section { align-items: center; color: var(--app-color-text); display: flex; font-size: var(--app-text-sm); font-weight: var(--app-font-semibold); gap: 6px; margin-bottom: 8px; }
  .cheques-form__section:not(:first-child) { border-top: 1px solid var(--app-color-border); margin-top: 4px; padding-top: 12px; }
  .cheques-form__section .material-symbols-rounded { color: var(--app-color-primary); font-size: 18px; }
  .cheques-form__amount input { font-size: 15px; font-weight: var(--app-font-semibold); text-align: right; }
  .cheques-form__expiration { align-items: center; border: 1px solid; border-radius: 999px; display: inline-flex; font-size: var(--app-text-xs); font-weight: var(--app-font-semibold); gap: 4px; margin-top: 12px; padding: 4px 10px; }
  .cheques-form__expiration .material-symbols-rounded { font-size: 15px; }
  .cheques-form__expiration--success { background: var(--app-color-success-soft); border-color: var(--app-color-success-border); color: var(--app-color-success); }
  .cheques-form__expiration--warning { background: var(--app-color-warning-soft); border-color: var(--app-color-warning-border); color: var(--app-color-warning-hover); }
  .cheques-form__expiration--danger { background: var(--app-color-danger-soft); border-color: var(--app-color-danger-border); color: var(--app-color-danger); }
  .cheques-form__switch { background: var(--app-color-surface-subtle); border: 1px solid var(--app-color-border); border-radius: 7px; margin-top: 6px; padding: 9px 12px; }
  .cheques-form__switch >>> .custom-control-label { color: var(--app-color-primary-active); font-size: var(--app-text-sm); font-weight: var(--app-font-semibold); }
  .cheques-form__switch small { color: var(--app-color-text-secondary); display: block; font-size: var(--app-text-xs); line-height: 1.35; margin-top: 4px; }
</style>
